//Hooks & Stores
import { get } from 'svelte/store';
import { devices_settings } from 'src/stores/device-runtime/status';
//Formating
import { format } from 'svelte-i18n';
//Services
import { gen2Request } from 'src/services/gen_2_http_reqs';
//Helpers
import { getWebIconStatuses } from './virtual_icons_helpers';
import { onSettingsResponse } from '../device_settings_helpers';
import { verifySettingUnsupportedFWVersion } from '../firmware_helpers';
//Types
import type { Device } from '../../types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type {
	VirtualComponentInterface,
	VirtualComponentTypes,
	VirtualWebIcons,
} from 'src/types/VirtualComponents';

export const saveVCSettings = async (
	device: Device,
	type: string,
	settings: VirtualComponentInterface['config'],
): Promise<SettingRequest> => {
	const res: SettingRequest = await gen2Request(device, `${type}.setconfig`, {
		id: settings.id,
		config: settings,
	});
	onSettingsResponse(res, device);
	return res;
};

export const getWizardVComponent = () => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('choose_virtual_component_type_tab', {
					default: 'Type',
				}),
				subtitle: $format('choose_virtual_component_label', {
					default: 'Choose virtual component type',
				}),
			},
			{
				title: $format('create_virtual_component_settings_tab', {
					default: 'Settings',
				}),
				subtitle: $format('virtual_component_modal_settings', {
					default: 'Virtual component settings',
				}),
			},
		];
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};

export const getWizardVGroup = () => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('create_virtual_group_tab', {
					default: 'Create virtual group',
				}),
				subtitle: '',
			},
			{
				title: $format('choose_virtual_component_group_tab', {
					default: 'Virtual components',
				}),
				subtitle: '',
			},
		];
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};

export const getInitVCSettings = (
	type: VirtualComponentTypes | 'group',
): VirtualComponentInterface['config'] => {
	const settings: VirtualComponentInterface['config'] = {
		name: '',
		persisted: false,
		meta: {
			cloud: [],
			ui: {
				icon: '',
				view: type === 'button' ? type : 'label',
			},
		},
	};
	if (type == 'number') {
		settings.min = 0;
		settings.max = 100;
		settings.meta.ui.step = 1;
		settings.meta.ui.unit = '';
	} else if (type == 'text') {
		settings.max_len = 255;
	}
	return settings;
};

/** Check if the Option Type of Virtual Component is supported by device's firmware
 * 		- If the `min_version` is not provided, it will return true
 * 		- If the `min_version` is provided, it will check if the device firmware version is supported
 * @param device - Device object
 * @param min_version - Minimum firmware version required for the virtual component type
 * @returns boolean - true if the VC Type option is supported by the device firmware version
 */
export const checkVCTypeOptionsFWSupport = (device: Device, min_version: string | undefined) => {
	const device_settings = devices_settings.getByRealId(device.id);
	return !min_version || !verifySettingUnsupportedFWVersion(device, device_settings, min_version);
};

export const getVCTypeOptions = (): {
	title: string;
	value: VirtualComponentTypes;
	min_version?: string;
}[] => {
	const $format = get(format);
	return [
		{
			title: $format('virtual_component_button', {
				default: 'button',
			}),
			value: 'button',
			min_version: '1.3.0', //`button` type is available from fw version 1.3.0
		},
		{
			title: $format('virtual_component_number', {
				default: 'number',
			}),
			value: 'number',
		},
		{
			title: $format('virtual_component_boolean', {
				default: 'boolean',
			}),
			value: 'boolean',
		},
		{
			title: $format('virtual_component_text', {
				default: 'text',
			}),
			value: 'text',
		},
		{
			title: $format('virtual_component_enum', {
				default: 'enum',
			}),
			value: 'enum',
		},
	];
};

/**
 *
 * @param iconId - Numeric value of the icon as per the VirtualWebIcons object
 * @returns VirtualWebIcons   - Object with icon details
 */
export const getWebIcon = (iconId: number): VirtualWebIcons => {
	return getWebIconStatuses().find((icon) => icon.value === iconId);
};
